import type { CityConfig } from "@/pages/city-template/types";

export const springfieldOrConfig: CityConfig = {
  vitalRecordsIcon: "ri-file-text-line",
  vitalRecordsLinks: [
    { icon: "ri-file-text-line", label: "Oregon Vital Records (Birth, Death, Marriage)", desc: "Request certified birth, death, and marriage certificates from Oregon Vital Records.", url: "https://www.oregon.gov/oha/PH/BirthDeathCertificates/Pages/index.aspx" },
    { icon: "ri-government-line", label: "Lane County Clerk Vital Records", desc: "Obtain vital records through the Lane County Clerk's office in Eugene.", url: "https://www.lanecounty.org/government/county_departments/clerk" },
    { icon: "ri-global-line", label: "VitalChek — Order Vital Records Online", desc: "Official online ordering partner of Oregon Vital Records for certified certificates.", url: "https://www.vitalchek.com/" },
  ],
  city: "Springfield",
  state: "Oregon",
  stateAbbr: "OR",
  slug: "springfield-or",
  route: "/cities/springfield-or",
  title: "Emergency Passport Springfield OR | Expedited Passport Lane County Oregon",
  metaDescription: "Emergency passport and expedited passport services in Springfield, OR — as fast as 24 hours. Fast Passport Center serves Springfield, Eugene, Lane County, and the southern Willamette Valley. Registered State Dept. courier. A+ BBB rated.",
  h1: "Emergency & Expedited Passport Services in Springfield, Oregon",
  heroSubtitle: "Springfield resident with an urgent passport need? As Eugene's twin city, you have the same international travel needs but limited local passport agency access. The nearest regional agency is in Seattle — 5 hours north. Fast Passport Center gets Springfield residents expedited passports in days, entirely remotely. No Seattle drive.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Springfield%20Oregon%20Willamette%20Valley%20cityscape%20with%20the%20McKenzie%20River%20flowing%20through%20lush%20green%20landscape%2C%20view%20of%20the%20Springfield%20Riverfront%2C%20downtown%20buildings%20with%20Springfield%20Oregon%20skyline%2C%20Cascade%20Mountains%20visible%20in%20the%20eastern%20background%2C%20Pacific%20Northwest%20scenic%20beauty%20with%20dramatic%20clouds%20and%20clear%20sky%2C%20photorealistic%20landscape%2C%20no%20text%20no%20people&width=1800&height=700&seq=springfield-or-hero-01&orientation=landscape",
  heroImageAlt: "Springfield Oregon with the McKenzie River and Cascade Mountains",
  geoPosition: "44.0462;-123.0220",
  geoPlacename: "Springfield, Oregon",
  lastModified: "2026-06-14",
  regionalAgency: {
    name: "Seattle Passport Agency",
    address: "915 2nd Ave Suite 500, Seattle WA 98174",
    distance: "About 300 miles north — approximately 5 hours by car",
  },
  nearbyCitiesZips: [
    { name: "Springfield", zips: "97477–97478" },
    { name: "Eugene", zips: "97401–97405" },
    { name: "Creswell", zips: "97426" },
    { name: "Cottage Grove", zips: "97424" },
    { name: "Junction City", zips: "97448" },
    { name: "Harrisburg", zips: "97446" },
    { name: "Veneta", zips: "97487" },
    { name: "Florence", zips: "97439" },
    { name: "Lebanon", zips: "97355" },
    { name: "Corvallis", zips: "97330–97333" },
    { name: "Albany", zips: "97321–97322" },
    { name: "Roseburg", zips: "97470–97471" },
  ],
  benefits: [
    { icon: "ri-computer-line", title: "Start 100% Online from Springfield", desc: "Complete your emergency or expedited passport order from Springfield — no drive to Seattle or Portland required." },
    { icon: "ri-truck-line", title: "Ship Overnight from Springfield or Eugene", desc: "FedEx and UPS locations in Springfield and Eugene serve the Lane County area for overnight passport document shipping." },
    { icon: "ri-shield-check-line", title: "Professional Document Review", desc: "Every application reviewed before submission — essential for Springfield applicants far from the nearest regional passport agency." },
    { icon: "ri-calendar-check-line", title: "Reserved Agency Submission Access", desc: "We submit through reserved Seattle Passport Agency channels — bypassing the public appointment system." },
  ],
  testimonials: [
    {
      name: "Catherine M.",
      location: "Springfield, OR",
      rating: 5,
      text: "Had a work trip to Europe with three weeks notice. Passport was expired by three months. Fast Passport Center got it renewed in 7 days. I shipped from the Springfield FedEx and had it back before the flight.",
      service: "Expedited Passport Renewal",
    },
    {
      name: "James K.",
      location: "Springfield, OR",
      rating: 5,
      text: "Family of four — all needed passports for a Mexico trip. The Springfield Post Office had a two-week wait for appointments. Fast Passport Center guided us through the Eugene Main Post Office step and expedited all four. Every passport back in 9 days.",
      service: "Expedited Child Passports",
    },
    {
      name: "Dr. Susan T.",
      location: "Eugene, OR",
      rating: 5,
      text: "University of Oregon faculty member — needed a passport renewed for an international conference in Amsterdam. Fast Passport Center handled it in 6 days while I was still teaching classes. I shipped from the Eugene UPS. Absolutely outstanding.",
      service: "Expedited Passport Renewal",
    },
  ],
  facilities: [
    {
      id: "springfield-post-main",
      name: "Springfield Main Post Office",
      type: "USPS Passport Acceptance",
      address: "2000 Gateway St",
      city: "Springfield",
      state: "OR",
      zip: "97477",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–5pm",
      distance: "Central Springfield — primary passport acceptance facility for Springfield residents",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Primary passport acceptance facility for Springfield. Appointment required — book well in advance during peak summer travel and UO term periods.",
      icon: "ri-mail-line",
    },
    {
      id: "eugene-post-main",
      name: "Eugene Main Post Office",
      type: "USPS Passport Acceptance",
      address: "520 Willamette St",
      city: "Eugene",
      state: "OR",
      zip: "97401",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–5pm, Sat 10am–2pm",
      distance: "Eugene — about 4 miles west of Springfield, largest acceptance facility in Lane County",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Eugene Main Post Office is the largest passport acceptance facility in Lane County — better availability than Springfield. Saturday morning hours are available.",
      icon: "ri-mail-line",
    },
    {
      id: "eugene-post-5th",
      name: "Eugene 5th Street Post Office",
      type: "USPS Passport Acceptance",
      address: "1601 E 5th Ave",
      city: "Eugene",
      state: "OR",
      zip: "97401",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–5pm",
      distance: "Eugene — about 5 miles west of Springfield",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "5th Street Eugene post office — alternate option for Springfield residents when the Eugene Main Post Office is booked.",
      icon: "ri-mail-line",
    },
    {
      id: "eugene-post-royal",
      name: "Eugene Royal Avenue Post Office",
      type: "USPS Passport Acceptance",
      address: "1375 W Royal Ave",
      city: "Eugene",
      state: "OR",
      zip: "97402",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–5pm",
      distance: "West Eugene — about 7 miles west of Springfield",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "West Eugene post office for Springfield and west Eugene residents. Limited appointment availability.",
      icon: "ri-mail-line",
    },
    {
      id: "lane-county-clerk",
      name: "Lane County Clerk's Office",
      type: "County Clerk Passport Acceptance",
      address: "125 E 8th Ave",
      city: "Eugene",
      state: "OR",
      zip: "97401",
      phone: "(541) 682-3604",
      hours: "Mon–Fri 8:30am–5pm",
      distance: "Eugene — about 4 miles west of Springfield, Lane County Courthouse",
      website: "https://www.lanecounty.org/government/county_departments/clerk",
      websiteLabel: "Lane County Clerk",
      notes: "Lane County Clerk in Eugene accepts passport applications. Often has better appointment availability than Springfield or Eugene post offices.",
      icon: "ri-government-line",
    },
    {
      id: "creswell-post",
      name: "Creswell Post Office",
      type: "USPS Passport Acceptance",
      address: "95 S 2nd St",
      city: "Creswell",
      state: "OR",
      zip: "97426",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4:30pm",
      distance: "Creswell — about 10 miles south of Springfield",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Creswell post office for south Lane County residents. Very limited appointment availability — Eugene Main Post Office is preferred.",
      icon: "ri-mail-line",
    },
  ],
  faqs: [
    {
      q: "Can I get an emergency passport from Springfield, Oregon?",
      a: "Yes. Fast Passport Center provides emergency passport processing for Springfield residents — as fast as 24 hours. The nearest regional passport agency is in Seattle (5 hours north). We serve Springfield entirely remotely: start online, ship overnight from Springfield or Eugene, and we submit through reserved agency channels.",
    },
    {
      q: "How fast can I renew my passport from Springfield, OR?",
      a: "Adult passport renewal from Springfield is available in as little as 3 business days through Fast Passport Center. Most adult renewals are by mail — no in-person appointment required. Ship overnight from Springfield or Eugene.",
    },
    {
      q: "Where is the best passport acceptance office near Springfield?",
      a: "Springfield has the Main Post Office (2000 Gateway St) as the primary local acceptance facility. The Eugene Main Post Office (4 miles west) is the best-staffed and most available option in the area, with Saturday morning hours. The Lane County Clerk in Eugene also offers good availability.",
    },
    {
      q: "Does Springfield, Oregon have a passport acceptance office?",
      a: "Yes — Springfield Main Post Office accepts passport applications. For the best availability in the Lane County area, Eugene Main Post Office is the preferred option. The nearest regional passport agency is in Seattle.",
    },
    {
      q: "Can I expedite a passport for a Mexico trip from Springfield?",
      a: "Yes. Mexico is a popular destination for Springfield residents — Puerto Vallarta, Cabo, and the Riviera Maya are common winter escapes. Fast Passport Center handles expedited passport renewals in 3–9 business days from Springfield.",
    },
    {
      q: "What airport do Springfield residents use for international travel?",
      a: "Springfield residents use Eugene Airport (EUG, 5 miles south) for regional connections and Portland International (PDX, 110 miles north) for direct international flights. PDX offers direct service to Europe, Japan, Mexico, and Canada.",
    },
    {
      q: "Can University of Oregon students and faculty get expedited passports?",
      a: "Yes. Fast Passport Center regularly serves the UO community in Eugene-Springfield for expedited passports for study abroad, international conferences, and academic research. The UO has one of the largest study abroad programs in the Pacific Northwest.",
    },
    {
      q: "How long does standard passport processing take from Springfield?",
      a: "Standard USPS mail processing from Springfield runs 6–13 weeks during normal periods, and longer during peak summer and spring break seasons. If your trip is within 10 weeks, expedited service is recommended.",
    },
    {
      q: "Can I renew my passport without visiting an office in Springfield?",
      a: "Yes — most adult Springfield residents renewing a passport can do so entirely by mail. Start online with Fast Passport Center, ship overnight from Springfield or Eugene, and we handle the rest. No office visit required.",
    },
    {
      q: "Can Springfield residents get a passport for a Canada trip?",
      a: "Yes. Canada is the most common international destination for Springfield residents — Vancouver BC, Whistler, and Victoria are accessible by car or short flight. A valid passport is required for air travel. Fast Passport Center handles expedited renewals in 3–9 business days.",
    },
  ],
  photoLocations: [
    { name: "Walmart — Springfield", address: "3000 Gateway St, Springfield, OR 97477", hours: "Daily 7 AM – 11 PM" },
    { name: "FedEx Office — Springfield", address: "1975 Olympic St, Springfield, OR 97477", hours: "Mon–Fri 7 AM – 10 PM, Sat–Sun 9 AM – 6 PM" },
    { name: "Walgreens — Springfield Main", address: "2015 Olympic St, Springfield, OR 97477", hours: "Daily 8 AM – 10 PM" },
    { name: "Walmart — Eugene", address: "4550 W 11th Ave, Eugene, OR 97402", hours: "Daily 7 AM – 11 PM" },
    { name: "FedEx Office — Eugene", address: "199 E 5th Ave, Eugene, OR 97401", hours: "Mon–Fri 7 AM – 10 PM, Sat–Sun 9 AM – 6 PM" },
    { name: "Walgreens — Eugene", address: "1740 W 11th Ave, Eugene, OR 97402", hours: "Daily 8 AM – 10 PM" },
    { name: "Costco Photo Center — Eugene", address: "2200 W 11th Ave, Eugene, OR 97402", hours: "Mon–Fri 10 AM – 8:30 PM, Sat–Sun 9:30 AM – 6 PM" },
    { name: "Rite Aid — Eugene", address: "199 E 7th Ave, Eugene, OR 97401", hours: "Daily 8 AM – 10 PM" },
    { name: "Walgreens — Eugene Coburg", address: "1235 Coburg Rd, Eugene, OR 97401", hours: "Daily 8 AM – 10 PM" },
    { name: "Walmart — Roseburg", address: "2150 NW Stewart Pkwy, Roseburg, OR 97471", hours: "Daily 7 AM – 11 PM" },
  ],
  dmvLocations: [
    {
      name: "Springfield Driver & Motor Vehicle Services",
      address: "3050 Gateway St, Springfield, OR 97477",
      hours: "Mon–Fri 8:30 AM – 5:00 PM",
      note: "Springfield DMV — driver licenses, REAL ID, and ID cards for Springfield and east Lane County residents.",
      featured: true,
    },
    {
      name: "Eugene Driver & Motor Vehicle Services",
      address: "2870 W 11th Ave, Eugene, OR 97402",
      hours: "Mon–Fri 8:30 AM – 5:00 PM",
      note: "Eugene DMV — larger Lane County office with expanded capacity for Eugene, Springfield, and west Lane County residents.",
      featured: false,
    },
    {
      name: "Cottage Grove Driver & Motor Vehicle Services",
      address: "1335 E Washington Ave, Cottage Grove, OR 97424",
      hours: "Mon–Fri 8:30 AM – 5:00 PM",
      note: "Cottage Grove DMV — serves south Lane County communities including Cottage Grove and Creswell.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "EUG",
      name: "Eugene Airport",
      distance: "Primary regional airport — about 5 miles south of Springfield",
      address: "28855 Lockheed Dr, Eugene, OR 97402",
      note: "Eugene Airport is the primary airport for Springfield residents, with connections to Portland, San Francisco, Los Angeles, Denver, and Seattle for onward international flights.",
      flightUrl: "https://www.eugene-or.gov/airport",
      flightLabel: "Eugene Airport Website",
      featured: true,
    },
    {
      code: "PDX",
      name: "Portland International Airport",
      distance: "About 110 miles north via I-5",
      address: "7000 NE Airport Way, Portland, OR 97218",
      note: "Portland International Airport offers direct international flights to Europe, Japan, Mexico, and Canada. Springfield residents typically drive or fly to PDX for direct international departures.",
      flightUrl: "https://www.flypdx.com/",
      flightLabel: "PDX Airport Website",
      featured: false,
    },
    {
      code: "SFO",
      name: "San Francisco International Airport",
      distance: "About 380 miles south via I-5",
      address: "San Francisco, CA 94128",
      note: "SFO is an alternative major international hub for Springfield travelers — accessible via a 6-hour drive south.",
      flightUrl: "https://www.flysfo.com/",
      flightLabel: "SFO Airport Website",
      featured: false,
    },
  ],
  services: [
    {
      icon: "ri-time-line",
      title: "Emergency Passport — Springfield & Eugene",
      description: "Last-minute international trip from Springfield? Business conference abroad? Fast Passport Center handles emergency passport processing — as fast as 24 hours. Seattle is 5 hours away; we serve you entirely remotely.",
      bullets: ["As fast as 24 hours processing", "Reserved Seattle agency submission", "Dedicated agent and live case tracking"],
      badge: "Same-Day Available",
      link: "https://fastpassportcenter.com/checkout/?passport=emergency-passport",
    },
    {
      icon: "ri-refresh-line",
      title: "Expedited Passport Renewal — Springfield, OR",
      description: "Passport expired? Springfield residents renew fast — ship overnight from Springfield or Eugene, and we submit through reserved agency channels. Back in your hands in days.",
      bullets: ["Fastest renewal option for Springfield residents", "Ship overnight from Springfield or Eugene", "Professional document review included"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-search-eye-line",
      title: "Lost Passport Replacement",
      description: "Lost your passport before a trip from Springfield? We guide Lane County travelers through DS-64 and expedited replacement — no Seattle trip required.",
      bullets: ["DS-64 reporting guidance", "Expedited replacement timelines", "Document review included"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport — Springfield Families",
      description: "Springfield families heading to Canada, Mexico, or internationally need child passports. We guide you through the Springfield or Eugene acceptance step — you handle the visit, we expedite from there.",
      bullets: ["Both parents typically required to consent", "Springfield or Eugene acceptance guidance", "Expedited after acceptance step"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-add-line",
      title: "First-Time Passport Applications",
      description: "First-time passport from Springfield? We guide you through the Springfield or Eugene acceptance step and handle expedited processing after.",
      bullets: ["Step-by-step acceptance guidance", "Photo location guide for Springfield area", "Expedited processing after clerk visit"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-truck-line",
      title: "Registered State Dept. Courier Service",
      description: "Fast Passport Center is a registered U.S. Department of State courier — submitting through reserved agency channels at prices 30–100% lower than retail expediting services.",
      bullets: ["State Dept. registered courier", "Best price guarantee", "Not affected by government shutdowns"],
      badge: "Registered Courier",
      link: "https://fastpassportcenter.com/checkout/",
    },
    {
      icon: "ri-edit-line",
      title: "Name Change & Other Passport Services",
      description: "Name change after marriage, damaged passport replacement, second passport — all available with expedited courier access for Springfield area residents.",
      bullets: ["Name change passports", "Damaged passport replacement", "Second passport for frequent travelers"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Same-day emergency passport processing for Springfield, Oregon travelers with urgent international departure needs, as fast as 24 hours." },
    { name: "Expedited Passport", description: "Expedited passport processing for Springfield and Lane County residents needing passports faster than standard USPS timelines." },
    { name: "Passport Renewal", description: "Fast passport renewal for Springfield, Oregon residents — ship overnight from Springfield or Eugene, we handle the rest." },
    { name: "Lost Passport Replacement", description: "Emergency passport replacement for Springfield travelers with lost or stolen passports before travel." },
    { name: "Child Passport", description: "Expedited child passport services for Springfield and Lane County families traveling to Canada, Mexico, or internationally." },
    { name: "Passport Courier Service", description: "Registered U.S. Department of State courier serving Springfield, Oregon and the Lane County area." },
  ],
  mistakes: [
    { icon: "ri-time-line", title: "Waiting Until the Last Week to Check Passport Validity", description: "Springfield residents often travel to Mexico, Canada, and Europe for work and family trips. Many discover expired passports during packing. Check your passport the day you book international travel — not the week before departure." },
    { icon: "ri-camera-off-line", title: "Passport Photo Rejected by the Government", description: "Photos from drug stores frequently fail for lighting, background, or expression issues. Fast Passport Center reviews photo specifications before submission to prevent rejections." },
    { icon: "ri-file-damage-line", title: "Missing Signatures or Incomplete Forms", description: "One missing signature causes weeks of delay. Our pre-submission review catches all form errors before they reach the agency." },
    { icon: "ri-pen-nib-line", title: "Using the Wrong Birth Certificate or Document", description: "Short-form or abstract birth certificates are typically rejected. We specify the exact format required for your state of birth." },
    { icon: "ri-map-pin-time-line", title: "Ignoring the Six-Month Validity Rule", description: "Canada, Mexico, and most European countries require your passport to be valid for 6 months beyond your return date. A passport with 3 months left is functionally expired." },
    { icon: "ri-calendar-close-line", title: "UO Students Applying Too Late for Study Abroad", description: "University of Oregon study abroad programs have strict document submission deadlines. UO students should start their passport applications as soon as they are accepted into the program — not when the program checklist arrives." },
  ],
  steps: [
    { number: "01", icon: "ri-computer-line", title: "Start Your Order Online", description: "Choose your processing speed based on your Springfield travel date. Complete your secure order in minutes." },
    { number: "02", icon: "ri-file-list-3-line", title: "Receive Your Personalized Checklist", description: "We send a personalized checklist with Springfield and Lane County area acceptance location guidance." },
    { number: "03", icon: "ri-send-plane-line", title: "Ship Documents Overnight from Springfield or Eugene", description: "Drop off at FedEx or UPS in Springfield or Eugene — both serve the Lane County area with overnight shipping." },
    { number: "04", icon: "ri-building-2-line", title: "We Submit Through Reserved Agency Access", description: "We submit through reserved Seattle Passport Agency slots — no waiting for public appointments or 5-hour drives." },
    { number: "05", icon: "ri-passport-line", title: "Passport Shipped to Your Springfield Address", description: "Your completed passport ships directly to your Springfield or Lane County address. Track status in real time." },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Must drive to Seattle — 5 hours each way",
        "Life-or-death emergencies only",
        "Very limited appointment availability",
        "Not practical for Springfield residents",
      ],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Ship from Springfield or Eugene overnight",
        "Document review included",
        "Dedicated agent on your case",
        "Daily status updates",
        "Best price guarantee",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service USPS",
        "Document review included",
        "Dedicated agent",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "Standard mail — no courier access",
        "6–13 weeks; longer during peak season",
        "In-person clerk appointment required",
        "No document review — errors cause rejection",
        "No dedicated agent or tracking",
      ],
      highlight: false,
      muted: true,
    },
  ],
  comparisonRows: [
    { feature: "Appointment Required", agency: "Yes — limited Seattle slots, weeks out", fpc: "No appointment needed to start" },
    { feature: "In-Person Visit", agency: "Must drive to Seattle (5 hours each way from Springfield)", fpc: "Ship overnight from Springfield or Eugene" },
    { feature: "Processing Speed", agency: "Strict eligibility — urgent travel only", fpc: "As fast as 24 hours — multiple tiers" },
    { feature: "Document Review", agency: "None — errors cause rejection", fpc: "Professional review included" },
    { feature: "Lane County Accessibility", agency: "Not designed for Springfield/Eugene residents", fpc: "Fully remote — ship from Springfield or Eugene" },
    { feature: "Government Shutdowns", agency: "May be impacted", fpc: "Not affected" },
    { feature: "Best Price Guarantee", agency: "Govt. fees + 10-hour Seattle round trip", fpc: "Best price guarantee — no travel cost" },
  ],
  stickyNavLinks: [
    { label: "Processing Options", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Acceptance Locations", href: "#acceptance-facilities" },
    { label: "vs. Regional Agency", href: "#comparison" },
    { label: "FAQ", href: "#faq" },
    { label: "Local Resources", href: "#local-resources" },
  ],
  internalLinks: [
    { name: "Expedited passport services in Eugene", route: "/cities/eugene" },
    { name: "Emergency passport help in Albany", route: "/cities/albany-or" },
    { name: "Fast passport renewal in Corvallis", route: "/cities/corvallis" },
    { name: "Rush passport services in Salem", route: "/cities/salem" },
    { name: "Expedited passports for Oregon travelers", route: "/states/oregon" },
  ],
  fpcOfficeNote: "After placing your order online, we send your personalized checklist and overnight shipping instructions. Ship from FedEx or UPS in Springfield or Eugene. No office visit required for most services.",
  fpcOfficeDistance: "Serving Springfield and the entire Lane County area remotely",
  serviceAreaIntro: "Whether you're in Springfield, Eugene, Creswell, Cottage Grove, or anywhere in Lane County — Fast Passport Center provides emergency and expedited passport processing without the 5-hour drive to Seattle.",
  nearbyCitiesIntro: "We serve all Lane County zip codes including Springfield, Eugene, Creswell, Cottage Grove, Veneta, Florence, Junction City, Harrisburg, and west Lane County communities.",
  howItWorksIntro: "Getting an emergency or expedited passport from Springfield is simpler than a 5-hour drive to Seattle. Most Lane County clients complete the process without visiting an office.",
  mistakesIntro: "These are the most common mistakes that cause delays for Springfield and Lane County passport applicants. Our review process prevents all of them.",
  testimonialsIntro: "Real reviews from Springfield, Eugene, and Lane County travelers who needed their passports fast.",
  faqIntro: "The most common questions Springfield and Lane County residents ask about emergency and expedited passport services.",
  statePageRoute: "/states/oregon",
  statePageName: "Oregon",
  travelTiming: {
    title: "Travel & Passport Timing for Springfield and Lane County Residents",
    intro: "Springfield is Eugene's twin city — the second-largest metro in the Oregon Coast Range and southern Willamette Valley. The University of Oregon, Lane Community College, the Oregon Coast, and the Cascade Mountains create a diverse international travel profile that ranges from academic travel to coastal escapes to Pacific Rim business trips.",
    cards: [
      {
        icon: "ri-graduation-cap-line",
        title: "University of Oregon Study Abroad & Academic Travel",
        subtitle: "UO has one of the largest study abroad programs in the Pacific Northwest",
        description: "The University of Oregon in Eugene — adjacent to Springfield — sends hundreds of students abroad annually through programs in Europe, Asia, Latin America, and the Pacific. UO faculty travel internationally for research, conferences, and visiting positions. Study abroad programs have strict passport submission deadlines — often 4–6 months before departure — and visa requirements vary by country.",
        tip: "UO students: check your passport the moment you are accepted to a study abroad program. Most programs require passport submission 3–6 months before departure. Start the process immediately — don't wait for the program orientation.",
      },
      {
        icon: "ri-flight-takeoff-line",
        title: "Eugene Airport (EUG) & Portland International (PDX) Access",
        subtitle: "EUG is 5 miles south; PDX is 110 miles north for direct international flights",
        description: "Springfield residents use Eugene Airport (EUG) for regional connections to Portland, San Francisco, Los Angeles, Denver, and Seattle. For direct international flights, Portland International (PDX, 110 miles north) offers nonstop service to Amsterdam, London, Tokyo, Reykjavik, Mexico City, and Vancouver BC. The drive to PDX is approximately 2 hours via I-5.",
        tip: "If you need direct international service from PDX, check your passport when booking the flight. The 2-hour Springfield-to-PDX drive adds to your departure buffer — don't let an expired passport add another 5 hours to Seattle.",
      },
      {
        icon: "ri-sun-cloudy-line",
        title: "Mexico & Oregon Coast Winter Travel",
        subtitle: "Mexico beach trips and coastal escapes for Lane County residents November through April",
        description: "Lane County residents escape to Mexico, Hawaii, and the Oregon Coast during the cold, wet months. Puerto Vallarta, Cabo, and the Riviera Maya are popular winter destinations. The Oregon Coast (Florence, Yachats, Newport) is accessible year-round and requires a passport for international cruise departures. These bookings happen in September–October.",
        tip: "Book your winter Mexico trip in September, then immediately check your passport. If it expires before May the following year, renew it now. Fast Passport Center expedites renewals in 3–9 business days from Springfield.",
      },
      {
        icon: "ri-map-pin-2-line",
        title: "Canada, British Columbia & the Pacific Northwest",
        subtitle: "BC travel is the most common international destination for Springfield residents",
        description: "British Columbia is the top international destination for Springfield and Lane County residents — Vancouver BC for city travel, Whistler for skiing, Victoria for heritage, and the Gulf Islands for outdoor recreation. BC is accessible by car (I-5 north) or short flights from PDX or EUG. A valid passport is required for air travel, and recommended for land crossings.",
        tip: "BC summer trips are booked in February–March. Check your passport when booking — many Springfield residents discover expired passports during spring trip planning that were last used for a pre-pandemic BC trip.",
      },
    ],
    callout: {
      icon: "ri-landscape-line",
      title: "Springfield, Oregon — Eugene's Twin City and the Southern Willamette Valley Gateway",
      description: "Springfield shares the Lane County metro with Eugene — one of the Pacific Northwest's most dynamic university communities. The University of Oregon, the Oregon Coast, and the Cascade Mountains create a diverse international travel profile. Fast Passport Center serves Springfield, Eugene, and all Lane County residents with the same expedited passport expertise as Portland-area residents — without the Seattle drive.",
    },
  },
  destinations: {
    title: "Where Springfield Travelers Go — And Why Passports Get Urgent",
    intro: "These are the destinations we see most from Springfield and Lane County clients. The combination of UO academic travel, family Mexico trips, BC outdoor recreation, and Pacific Coast cruises creates a diverse passport urgency profile.",
    stats: [
      { stat: "300 mi", label: "to Seattle Passport Agency — nearest regional office from Springfield" },
      { stat: "110 mi", label: "to Portland International Airport (PDX) — primary direct international hub" },
      { stat: "5 mi", label: "to Eugene Airport (EUG) — regional connection hub for Springfield" },
      { stat: "2–9 Days", label: "fastest expedited passport processing available for Springfield residents" },
    ],
    destinations: [
      { name: "British Columbia, Canada", context: "Vancouver BC, Whistler skiing, Victoria heritage, and the Gulf Islands — top destination for Springfield and Lane County", urgency: "Spring/summer BC bookings; passport issues discovered during seasonal trip planning", icon: "ri-map-pin-2-line" },
      { name: "Mexico — Puerto Vallarta, Cabo, Cancun", context: "Winter and spring beach escapes for Lane County families — November through April", urgency: "September–October Mexico bookings; passports found expired during departure prep", icon: "ri-sun-cloudy-line" },
      { name: "Europe — UK, France, Spain, Italy", context: "UO study abroad programs, academic conferences, and heritage travel for Springfield area residents", urgency: "Study abroad program deadlines; academic conference registration; passport issues discovered at booking", icon: "ri-building-2-line" },
      { name: "Japan & East Asia", context: "UO research partnerships, teaching English abroad, and cultural exchange programs", urgency: "Teaching program and research deadlines require passport months in advance; visa applications need validity", icon: "ri-global-line" },
      { name: "Alaska — Inside Passage Cruises", context: "Alaska cruises from Seattle are popular with Lane County families — passports required for Canadian ports", urgency: "Spring cruise bookings; passport issues discovered at cruise check-in", icon: "ri-ship-line" },
      { name: "Costa Rica & Central America", context: "Eco-adventure travel for Lane County outdoor community — dry season December through April", urgency: "Fall bookings for winter travel; passport expiration discovered at booking time", icon: "ri-leaf-line" },
    ],
    crossLinksTitle: "Explore Nearby Oregon Service Areas",
    crossLinksDesc: "We serve travelers throughout the southern Willamette Valley and Lane County with the same expedited passport expertise.",
    crossLinks: [
      { name: "Eugene", route: "/cities/eugene" },
      { name: "Albany", route: "/cities/albany-or" },
      { name: "Corvallis", route: "/cities/corvallis" },
    ],
  },
};